import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { writeFileXLSX, utils } from "xlsx";
import {
  Button,
  ContentBox,
  ContentPage,
  DropdownAction,
  FullTable,
  ModalDefault,
  NoResults,
  PageActions,
  StatePill,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import { fixedAdjustmentCompetence, isCompetenceOpen } from "@helpers/fixedAdjustmentCompetence";
import { hasPermission } from "@helpers/permissions";
import useService from "../../../../hooks/useService";
import HelpVideoButton from "../../../../components/HelpVideoButton";
import MarkPaidModal from "../../../../components/MarkPaidModal";
import { formatDate, maskDocument, formatPhone, scheduleHasValue } from "../../../../helpers";
import {
  DomainStatus,
  escapeHtml,
  formatBRL,
  formatCompetencia,
  formatDate as formatDateText,
  usePageHeader,
} from "@components/ow";
import {
  CompetenceCell,
  Inline,
  InvoiceStatusCell,
  PaymentFilters,
  SCHEDULE_STATUS,
  STATUS_DATA,
  STATUS_LABELS,
  Stack,
  balanceLabel,
} from "../../Common/PaymentSchedules";

// Página da API por requisição: uma das opções do seletor da FullTable (10, 25, 50, 100)
const PAGE_SIZE = 25;

/** Colunas bancárias do Excel: o back só manda esses dados ao ADM e a "Ver dados bancários". */
const BANK_COLUMNS = ["Chave Pix", "Banco", "Agência", "Conta", "Tipo Conta"];

const BREADCRUMB = [{ page: "Pagamentos", route: "/c/payment-schedules" }];

export default function PaymentSchedules() {
  // "Ver cronogramas" só lê: marcar como pago segue só ADM
  const isAdmin = JSON.parse(localStorage.getItem("user_ow") || "{}")?.role === "ADM";
  const navigate = useNavigate();
  const paymentScheduleService = useService("payment-schedules");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [paginationData, setPaginationData] = useState({
    totalRegisters: 0,
    list: [],
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState(STATUS_DATA[0]);
  // Competência { month, year }; null = todas
  const [filterCompetence, setFilterCompetence] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [scheduleToPay, setScheduleToPay] = useState(null);
  // Só a resposta da última requisição vale (filtros seguidos podem voltar fora de ordem)
  const lastRequest = useRef(0);

  usePageHeader({ title: "Pagamentos", breadcrumb: BREADCRUMB });

  const statusValue = filterStatus.value;
  const hasFilter = !!statusValue || !!filterCompetence;

  useEffect(() => {
    fetchSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, statusValue, filterCompetence]);

  /** Filtros comuns à lista e à exportação. */
  function filterParams() {
    const params = [];
    if (statusValue) params.push(`status=${statusValue}`);
    if (filterCompetence) {
      params.push(`reference_month=${filterCompetence.month}`, `reference_year=${filterCompetence.year}`);
    }
    return params;
  }

  async function fetchSchedules() {
    const request = ++lastRequest.current;
    setLoading(true);
    try {
      // Visão da empresa: sem scope=company o back devolve só os cronogramas de quem chama
      const query = [`scope=company`, `page=${pagination.pageIndex + 1}`, `limit=${pagination.pageSize}`, ...filterParams()].join("&");
      const result = await paymentScheduleService.list(query);
      if (request !== lastRequest.current) return;

      if (result && result.data && result.data.list) {
        setPaginationData(result.data);
      } else if (result && typeof result === "string") {
        throw new Error(`API Error: ${result}`);
      } else {
        throw new Error("Erro ao carregar cronogramas - resposta inválida");
      }
    } catch (error) {
      if (request !== lastRequest.current) return;
      console.error(error);
      const errorMessage = error?.response?.data?.msg || error?.message || "Erro ao carregar cronogramas";
      toast.error(errorMessage);
      setPaginationData({
        totalRegisters: 0,
        list: [],
        totalPages: 1,
      });
    }
    setLoading(false);
  }

  const resetPage = () => setPagination((prev) => ({ ...prev, pageIndex: 0 }));

  function changeStatus(option) {
    setFilterStatus(option);
    resetPage();
  }

  function changeCompetence(competence) {
    setFilterCompetence(competence);
    resetPage();
  }

  function clearFilters() {
    setFilterStatus(STATUS_DATA[0]);
    setFilterCompetence(null);
    resetPage();
  }

  function handleMarkAsPaid(schedule) {
    // Sem valor definido não há o que dar baixa: o back recusa
    if (!scheduleHasValue(schedule)) {
      toast.error("Este cronograma ainda não tem valor definido. Defina o valor antes de registrar o pagamento.");
      return;
    }
    setScheduleToPay(schedule);
  }

  function activeFiltersText() {
    const parts = [];
    if (statusValue) parts.push(`Status: ${STATUS_LABELS[statusValue]}`);
    if (filterCompetence) parts.push(`Competência: ${formatCompetencia(filterCompetence)}`);
    return parts.join(" · ");
  }

  async function handleExportExcel() {
    setLoadingExport(true);
    try {
      const query = ["scope=company", ...filterParams()].join("&");
      const result = await paymentScheduleService.exportAll(query);

      if (!result || result.error || !result.data) {
        throw new Error(result?.msg || "Erro ao buscar dados para exportação");
      }

      const schedules = result.data;

      if (schedules.length === 0) {
        toast.info("Nenhum cronograma encontrado para exportar");
        setLoadingExport(false);
        setShowExportModal(false);
        return;
      }

      const sheetData = schedules.map((s) => ({
        "Nome Favorecido": s.user?.name || "-",
        "CPF/CNPJ": maskDocument(s.user?.cnpj || s.user?.document) || "-",
        "Valor": s.expected_value ? parseFloat(s.expected_value) : 0,
        "Período": `${String(s.reference_month).padStart(2, "0")}/${s.reference_year}`,
        "Vencimento": s.due_date ? formatDate(s.due_date) : "-",
        "Chave Pix": s.user?.bankData?.pix_key || "-",
        "Banco": s.user?.bankData?.bank_name
          ? `${s.user.bankData.bank_name}${s.user.bankData.bank_code ? ` (${s.user.bankData.bank_code})` : ""}`
          : "-",
        "Agência": s.user?.bankData?.bank_agency || "-",
        "Conta": s.user?.bankData?.bank_account
          ? `${s.user.bankData.bank_account}${s.user.bankData.bank_account_digit ? "-" + s.user.bankData.bank_account_digit : ""}`
          : "-",
        "Tipo Conta": s.user?.bankData?.bank_account_type || "-",
        "Telefone": formatPhone(s.user?.phone) || "-",
        "E-mail": s.user?.email || "-",
        "Status": STATUS_LABELS[s.status] || s.status,
        // Avisa quem for pagar que o valor da linha ainda nao fechou.
        "Situação": isCompetenceOpen(s) ? "Apuração parcial" : "Definitiva",
      }));

      const widths = {
        "Nome Favorecido": 30,
        "CPF/CNPJ": 20,
        "Valor": 15,
        "Período": 12,
        "Vencimento": 12,
        "Chave Pix": 30,
        "Banco": 25,
        "Agência": 10,
        "Conta": 15,
        "Tipo Conta": 15,
        "Telefone": 18,
        "E-mail": 30,
        "Status": 12,
        "Situação": 18,
      };
      if (!isAdmin && !hasPermission("users:read_sensitive")) {
        for (const row of sheetData) for (const col of BANK_COLUMNS) delete row[col];
      }

      const ws = utils.json_to_sheet(sheetData);

      ws["!cols"] = Object.keys(sheetData[0]).map((col) => ({ wch: widths[col] || 15 }));

      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, "Cronogramas");

      const now = new Date();
      const dateStr = `${String(now.getDate()).padStart(2, "0")}_${String(now.getMonth() + 1).padStart(2, "0")}_${now.getFullYear()}`;
      writeFileXLSX(wb, `cronogramas_pagamento_${dateStr}.xlsx`);

      toast.success("Exportação realizada com sucesso!");
      setShowExportModal(false);
    } catch (error) {
      console.error(error);
      const errorMessage = error?.response?.data?.msg || error?.message || "Erro ao exportar cronogramas";
      toast.error(errorMessage);
    }
    setLoadingExport(false);
  }

  const detailsRoute = (schedule) => `/c/payment-schedules/${schedule.id}`;

  function valueCell(schedule) {
    const competence = fixedAdjustmentCompetence(schedule);
    const hasValue = schedule.expected_value != null && schedule.expected_value !== "";
    return (
      <Stack $gap="2px">
        <Inline>
          {hasValue ? formatBRL(schedule.expected_value) : "-"}
          {/* A coluna é comparativa: o ADM soma prestadores, então o dinheiro continua
              a manchete. A pílula e o saldo dizem que o número ainda muda. */}
          {isCompetenceOpen(schedule) ? <StatePill $text="Parcial" $variant="orange" $size="small" /> : null}
          {competence?.isFrozen ? <StatePill $text="Encerrada" $variant="gray" $size="small" /> : null}
        </Inline>
        {competence?.isOpen ? (
          <Text $text={balanceLabel(competence.balanceHours)} $variant="caption_medium" $color="N400" />
        ) : null}
      </Stack>
    );
  }

  const columns = [
    {
      id: "user",
      header: "Prestador",
      size: 240,
      maxSize: 320,
      cell: ({ row }) => (
        <div>
          <Text $text={escapeHtml(row.original.user?.name || "-")} $variant="paragraph_small_bold" $color="N900" />
          {row.original.user?.company_name ? (
            <Text $text={escapeHtml(row.original.user.company_name)} $variant="caption_medium" $color="N400" />
          ) : null}
        </div>
      ),
    },
    {
      id: "competence",
      header: "Competência",
      size: 200,
      cell: ({ row }) => <CompetenceCell schedule={row.original} />,
    },
    { id: "due_date", header: "Vencimento", size: 110, cell: ({ row }) => (row.original.due_date ? formatDateText(row.original.due_date) : "-") },
    { id: "expected_value", header: "Valor esperado", size: 190, cell: ({ row }) => valueCell(row.original) },
    {
      id: "actual_value",
      header: "Valor pago",
      size: 120,
      cell: ({ row }) => (Number(row.original.actual_value) ? formatBRL(row.original.actual_value) : "-"),
    },
    {
      id: "status",
      header: "Status",
      size: 110,
      cell: ({ row }) => <DomainStatus domain="pagamento" value={SCHEDULE_STATUS[row.original.status] || row.original.status} />,
    },
    { id: "invoice", header: "Nota fiscal", size: 120, cell: ({ row }) => <InvoiceStatusCell schedule={row.original} /> },
    {
      id: "actions",
      // O "Ações" da lib tem 70px; abaixo disso a seta sai da célula
      size: 80,
      minSize: 80,
      maxSize: 80,
      align: "right",
      cell: ({ row }) => (
        <DropdownAction
          options={[
            { label: "Ver detalhes", action: () => navigate(detailsRoute(row.original)) },
            {
              label: "Marcar como pago",
              action: () => handleMarkAsPaid(row.original),
              show: isAdmin && row.original.status === "PENDING" && row.original.taxInvoice?.status === "APPROVED",
            },
          ]}
        />
      ),
    },
  ];

  const noResults = hasFilter ? (
    <NoResults notResultText="Nenhum pagamento encontrado" includeText="Limpar filtros" includeAction={clearFilters} />
  ) : (
    <NoResults notResultText="Nenhum pagamento por aqui" />
  );

  const filtersText = activeFiltersText();

  return (
    <ContentPage>
      <PageActions>
        <HelpVideoButton videoKey={["companyPaymentSchedule", "companyFixedValueBelow"]} />
        <Button $text="Exportar" $variant="outline" $action={() => setShowExportModal(true)} />
      </PageActions>

      <ContentBox>
        <Stack $gap="24px">
          <PaymentFilters
            id="payment-schedules-competence"
            status={filterStatus}
            onStatusChange={changeStatus}
            competence={filterCompetence}
            onCompetenceChange={changeCompetence}
            onClear={clearFilters}
          />

          <FullTable
            data={paginationData.list}
            columns={columns}
            rowCount={paginationData.totalRegisters}
            pagination={pagination}
            setPagination={setPagination}
            getRowId={(row) => String(row.id)}
            isLoading={loading}
            onRowClick={(schedule) => navigate(detailsRoute(schedule))}
            noResults={noResults}
          />
        </Stack>
      </ContentBox>

      {showExportModal && (
        <ModalDefault
          $showModal={showExportModal}
          $setShowModal={() => !loadingExport && setShowExportModal(false)}
          $noClose={loadingExport}
          $size="medium"
          $title="Exportar pagamentos"
          $contentBackground="N0"
          $footer={
            <>
              <Button
                $text="Cancelar"
                $variant="outline"
                $action={() => setShowExportModal(false)}
                $isDisabled={loadingExport}
                $noWidth
              />
              <Button $text="Exportar" $variant="primary" $action={handleExportExcel} $isLoading={loadingExport} $noWidth />
            </>
          }
        >
          <Stack $gap="12px">
            <Text
              $text="Deseja exportar os cronogramas de pagamento para uma planilha Excel?"
              $variant="paragraph_small_medium"
              $color="N700"
            />
            <Text
              $text={filtersText ? "<strong>Filtros ativos:</strong> %s" : "Nenhum filtro aplicado — todos os cronogramas serão exportados."}
              $replaces={filtersText ? [filtersText] : undefined}
              $variant="paragraph_small_medium"
              $color="N700"
              $colorEmphasis="N900"
            />
          </Stack>
        </ModalDefault>
      )}

      {scheduleToPay && (
        <MarkPaidModal
          schedule={scheduleToPay}
          onClose={() => setScheduleToPay(null)}
          onPaid={() => {
            setScheduleToPay(null);
            fetchSchedules();
          }}
        />
      )}
    </ContentPage>
  );
}
